/* eslint-disable no-underscore-dangle */
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IdType, SortingRule } from "react-table";
import {
  HvButton,
  HvGrid,
  HvTag,
  HvTypography,
} from "@hitachivantara/uikit-react-core";
import {
  useHvTable,
  HvTableColumnConfig,
  useHvBulkActions,
  useHvPagination,
  useHvRowSelection,
  useHvSortBy,
  HvCellProps,
} from "@hitachivantara/uikit-react-lab";
import { Delete, Edit } from "@hitachivantara/uikit-react-icons";

import SimpleTable, {
  textColumn,
  actionsColumn,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
} from "components/common/SimpleTable";

import {
  useWorkflows,
  RuleSearchDetail,
  RuleStatus,
  deleteAllWorkflow,
} from "lib/api";
import { useSnackbar } from "lib/provider/SnackbarProvider";
import { NotificationContext } from "lib/context/NotificationContext";

import {
  ApplyFilters,
  Filter,
  getApiApplyFiltersFromHvFilterGroupsSeletedItems,
} from "lib/utils/filterUtils";
import DeleteDialog from "components/common/DeleteDialog";
import WorkflowTableControls from "../WorkflowTableControls";

interface WorkflowDataRow extends Record<string, unknown> {
  id: string;
  name: string;
  description: string;
  status: number;
  author: string;
}

const sortFieldMap = {
  name: "name",
  description: "description",
  status: "status",
  author: "created_by",
  typeName: "type_id",
};

const DEFAULT_SORT = [
  {
    id: "name",
    desc: false,
  },
];

const WorkflowTable: React.FC = () => {
  const { t } = useTranslation("workflow");
  const [tableData, setTableData] = useState<WorkflowDataRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const [filter, setFilter] = useState<ApplyFilters[]>([]);
  const history = useHistory();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { setShowBanner, setBannerMessage } = useContext(NotificationContext);

  const columns = useMemo(
    (): HvTableColumnConfig<WorkflowDataRow>[] => [
      textColumn({
        Header: t("columns.name") as string,
        accessor: "name",
        style: { width: "20%" },
      }),
      textColumn({
        Header: t("columns.description") as string,
        accessor: "description",
      }),
      {
        Header: t("columns.status") as string,
        accessor: "status",
        style: { width: 120 },
        Cell: ({ value }: HvCellProps<WorkflowDataRow>) => {
          return (
            <HvTag
              label={
                <HvTypography variant="normalText">
                  {t(`filter.status${value}`)}
                </HvTypography>
              }
              type="categorical"
              color={value === RuleStatus.Active ? "#b9d696" : "#ebbecb"}
              tabIndex={-1}
            />
          );
        },
      },
      textColumn({
        Header: t("columns.author") as string,
        accessor: "author",
        style: { width: "20%" },
      }),
      textColumn({
        Header: t("columns.eventType") as string,
        accessor: "typeName",
        style: { width: "20%" },
      }),
      actionsColumn({
        id: "edit",
        Cell: ({ row }: HvCellProps<WorkflowDataRow>) => {
          return (
            <HvButton
              aria-label={t("labels.editWorkflowButton")}
              icon
              onClick={() =>
                history.push(`/settings/workflow/${row.original.id}`)
              }
            >
              <Edit />
            </HvButton>
          );
        },
      }),
    ],
    [history, t]
  );

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [baseFilter, setBaseFilter] = useState<Filter[]>([]);
  const [selectedIndexFilter, setSelectedIndexFilter] = useState<number[][]>();

  const [sortBy, setSortBy] =
    useState<SortingRule<WorkflowDataRow>[]>(DEFAULT_SORT);

  const sortField = sortFieldMap[sortBy[0].id as keyof typeof sortFieldMap];

  const [selectedIds, setSelectedIds] = useState<
    Record<IdType<WorkflowDataRow>, boolean>
  >({});
  const [calculateFilters, setcalculateFilters] = useState<boolean>(true);
  const { data, error, mutate } = useWorkflows(
    {
      pageNr: page + 1,
      pageSize: size,
      orderBy: encodeURIComponent(sortField),
      order: sortBy[0].desc ? "desc" : "asc",
      searchKey,
      calculateFilters,
    },
    { suspense: tableData.length === 0 },
    filter
  );
  useEffect(() => {
    if (data && (data.data?.filters?.length ?? 0) > 0) {
      setBaseFilter(data.data.filters ?? []);
    }
    setcalculateFilters(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (data) {
      setTableData(
        data.data?.results?.map((workflow: RuleSearchDetail) => ({
          id: workflow._id,
          name: workflow.name,
          description: workflow.description,
          status: workflow.status,
          author: workflow.created_by_name,
          typeName: workflow.type_name,
        })) ?? []
      );
      setTotalCount(data.totalCount ?? 0);
    }
  }, [data]);

  const initialState = useMemo(() => {
    const entries = tableData.map<[string, boolean]>((v) => [
      v.id,
      v.status === RuleStatus.Active,
    ]);

    return {
      pageIndex: page,
      pageSize: size,
      sortBy,
      lockedSelectionRowIds: (entries && Object.fromEntries(entries)) ?? {},
      selectedRowIds: selectedIds,
    };
  }, [tableData, sortBy, page, size, selectedIds]);

  const labels = useMemo(
    () => ({
      selectRowCheckBoxAriaLabel: t("labels.selectCheckbox"),
    }),
    [t]
  );

  const bulkActions = useMemo(
    () => [
      {
        id: "archive",
        label: t("labels.bulkDeleteButton"),
        icon: <Delete />,
        onClick: () => setIsDeleteDialogOpen(true),
      },
    ],
    [t]
  );

  const instance = useHvTable<WorkflowDataRow>(
    {
      columns,
      data: tableData || [],
      getRowId: useCallback((v: WorkflowDataRow): string => `${v.id}`, []),
      manualPagination: true,
      pageCount: Math.ceil((totalCount ?? 0) / (initialState?.pageSize ?? 1)),
      initialState,
      labels,
      aditivePageBulkSelection: true,
      subtractivePageBulkDeselection: true,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
    },
    useHvSortBy,
    useHvPagination,
    useHvRowSelection,
    useHvBulkActions
  );

  const {
    state: { selectedRowIds, pageIndex, pageSize, sortBy: sortByFromTable },
  } = instance;

  useEffect(() => setPage?.(pageIndex), [pageIndex, setPage]);
  useEffect(() => setSize?.(pageSize), [pageSize, setSize]);
  useEffect(() => {
    setSortBy?.(sortByFromTable);
    setPage?.(0);
  }, [sortByFromTable, setSortBy, setPage]);
  useEffect(() => setSelectedIds(selectedRowIds), [selectedRowIds]);

  const onSearchHandler = (value: string) => {
    setSearchKey(value);
    instance.gotoPage(DEFAULT_PAGE);
  };

  const onFilterHandler = (value: number[][]) => {
    setFilter(
      getApiApplyFiltersFromHvFilterGroupsSeletedItems(baseFilter, value)
    );
    setSelectedIndexFilter(value);
    /* {
      status: {
        filterValues: value[0] || [],
      },
      action: {
        filterValues: value[1] || [],
      },
      entityTypes: {
        filterValues: value[2] || [],
      },
    }); */
    instance.gotoPage(DEFAULT_PAGE);
  };

  const onDeleteClose = useCallback(
    (result, message, ids) => {
      setIsDeleteDialogOpen(false);

      if (ids) {
        const newSelectedIds = {
          ...selectedIds,
        };

        ids.forEach((id: string) => {
          delete newSelectedIds[id];
        });
        setSelectedIds(newSelectedIds);
      }

      if (result && ids) {
        enqueueSnackbar(message, {
          variant: "success",
        });
        setPage(DEFAULT_PAGE);
        mutate();
      } else if (message) {
        setBannerMessage(message);
        setShowBanner(true);
        mutate();
      }
    },
    [enqueueSnackbar, mutate, selectedIds, setBannerMessage, setShowBanner]
  );

  return (
    <HvGrid container spacing={2}>
      <HvGrid item xs={12}>
        <WorkflowTableControls
          onSearch={onSearchHandler}
          onFilter={onFilterHandler}
          filters={selectedIndexFilter}
          baseFilter={baseFilter}
        />
      </HvGrid>
      <HvGrid item xs={12}>
        <SimpleTable
          instance={instance}
          bulkActions={bulkActions}
          totalCount={totalCount}
          loading={!data && !error}
        />
        <DeleteDialog
          scope="workflow"
          isOpen={isDeleteDialogOpen}
          promiseAll={() => deleteAllWorkflow(Object.keys(selectedRowIds))}
          onClose={onDeleteClose}
          ids={Object.keys(selectedRowIds)}
        />
      </HvGrid>
    </HvGrid>
  );
};

export default WorkflowTable;
